const defineProgressLocale = (copy) => Object.freeze({
    ...copy,
    tutorialSteps: Object.freeze(copy.tutorialSteps.map((step) => Object.freeze(step))),
});

export const PROGRESS_LOCALES = Object.freeze({
    en: defineProgressLocale({
        title: 'Progress',
        subtitle: 'Your scores over time',
        overallTitle: 'Overall progress',
        progressIndex: 'Progress index',
        baselineHint: 'Compared with your first result',
        byGameTitle: 'By game',
        current: 'Current',
        best: 'Best',
        results: 'Results',
        noScoreYet: 'No score yet',
        playToTrack: 'Play to start tracking',
        trackedGames: 'Games tracked',
        totalResults: 'Total results',
        metricScore: 'Score',
        metricStreak: 'Streak',
        metricTime: 'Time',
        metricLevel: 'Level',
        trendUp: 'Up {value}',
        trendDown: 'Down {value}',
        trendFlat: 'No change',
        backToGames: 'Back to games',
        tutorialNext: 'Next',
        tutorialDone: 'Done',
        tutorialClose: 'Close tutorial',
        tutorialSteps: [
            ['SOPT Classic', 'Remember the center icon and the direction of the outer target.'],
            ['SOPT Advanced', 'Also remember which outer icon was different—the third answer makes the run harder.'],
            ['Game cards', 'Expand any game card to see a gameplay example, set its own level, and follow its own daily progress.'],
            ['Progress', 'See your overall trend and each game’s score history here.'],
        ],
    }),
    fr: defineProgressLocale({
        title: 'Progression',
        subtitle: 'Vos scores au fil du temps',
        overallTitle: 'Progression globale',
        progressIndex: 'Indice de progression',
        baselineHint: 'Comparé à votre premier résultat',
        byGameTitle: 'Par jeu',
        current: 'Actuel',
        best: 'Meilleur',
        results: 'Résultats',
        noScoreYet: 'Aucun score',
        playToTrack: 'Jouez pour commencer le suivi',
        trackedGames: 'Jeux suivis',
        totalResults: 'Résultats totaux',
        metricScore: 'Score',
        metricStreak: 'Série',
        metricTime: 'Temps',
        metricLevel: 'Niveau',
        trendUp: 'Hausse de {value}',
        trendDown: 'Baisse de {value}',
        trendFlat: 'Aucun changement',
        backToGames: 'Retour aux jeux',
        tutorialNext: 'Suivant',
        tutorialDone: 'Terminé',
        tutorialClose: 'Fermer le tutoriel',
        tutorialSteps: [
            ['SOPT Classic', 'Mémorisez l’icône centrale et la direction de la cible extérieure.'],
            ['SOPT Advanced', 'Mémorisez aussi l’icône extérieure différente : cette troisième réponse augmente la difficulté.'],
            ['Cartes des jeux', 'Dépliez une carte pour voir un exemple, régler son niveau et suivre sa progression quotidienne.'],
            ['Progression', 'Consultez ici votre tendance globale et l’historique des scores de chaque jeu.'],
        ],
    }),
    es: defineProgressLocale({
        title: 'Progreso',
        subtitle: 'Tus puntuaciones a lo largo del tiempo',
        overallTitle: 'Progreso general',
        progressIndex: 'Índice de progreso',
        baselineHint: 'Comparado con tu primer resultado',
        byGameTitle: 'Por juego',
        current: 'Actual',
        best: 'Mejor',
        results: 'Resultados',
        noScoreYet: 'Aún no hay puntuación',
        playToTrack: 'Juega para empezar el seguimiento',
        trackedGames: 'Juegos registrados',
        totalResults: 'Resultados totales',
        metricScore: 'Puntuación',
        metricStreak: 'Racha',
        metricTime: 'Tiempo',
        metricLevel: 'Nivel',
        trendUp: 'Sube {value}',
        trendDown: 'Baja {value}',
        trendFlat: 'Sin cambios',
        backToGames: 'Volver a los juegos',
        tutorialNext: 'Siguiente',
        tutorialDone: 'Listo',
        tutorialClose: 'Cerrar tutorial',
        tutorialSteps: [
            ['SOPT Classic', 'Recuerda el icono central y la dirección del objetivo exterior.'],
            ['SOPT Advanced', 'Recuerda también qué icono exterior era distinto; la tercera respuesta aumenta la dificultad.'],
            ['Tarjetas de juegos', 'Despliega una tarjeta para ver un ejemplo, ajustar su nivel y seguir su progreso diario.'],
            ['Progreso', 'Consulta aquí tu tendencia general y el historial de puntuaciones de cada juego.'],
        ],
    }),
    pt: defineProgressLocale({
        title: 'Progresso',
        subtitle: 'Suas pontuações ao longo do tempo',
        overallTitle: 'Progresso geral',
        progressIndex: 'Índice de progresso',
        baselineHint: 'Comparado ao seu primeiro resultado',
        byGameTitle: 'Por jogo',
        current: 'Atual',
        best: 'Melhor',
        results: 'Resultados',
        noScoreYet: 'Ainda sem pontuação',
        playToTrack: 'Jogue para começar a acompanhar',
        trackedGames: 'Jogos acompanhados',
        totalResults: 'Total de resultados',
        metricScore: 'Pontuação',
        metricStreak: 'Sequência',
        metricTime: 'Tempo',
        metricLevel: 'Nível',
        trendUp: 'Subiu {value}',
        trendDown: 'Caiu {value}',
        trendFlat: 'Sem mudança',
        backToGames: 'Voltar aos jogos',
        tutorialNext: 'Próximo',
        tutorialDone: 'Concluir',
        tutorialClose: 'Fechar tutorial',
        tutorialSteps: [
            ['SOPT Classic', 'Lembre o ícone central e a direção do alvo externo.'],
            ['SOPT Advanced', 'Lembre também qual ícone externo era diferente; a terceira resposta aumenta o desafio.'],
            ['Cartões dos jogos', 'Expanda um cartão para ver um exemplo, ajustar seu nível e acompanhar seu progresso diário.'],
            ['Progresso', 'Veja aqui sua tendência geral e o histórico de pontuação de cada jogo.'],
        ],
    }),
    it: defineProgressLocale({
        title: 'Progressi',
        subtitle: 'I tuoi punteggi nel tempo',
        overallTitle: 'Progressi complessivi',
        progressIndex: 'Indice di progresso',
        baselineHint: 'Rispetto al tuo primo risultato',
        byGameTitle: 'Per gioco',
        current: 'Attuale',
        best: 'Migliore',
        results: 'Risultati',
        noScoreYet: 'Nessun punteggio',
        playToTrack: 'Gioca per iniziare il monitoraggio',
        trackedGames: 'Giochi monitorati',
        totalResults: 'Risultati totali',
        metricScore: 'Punteggio',
        metricStreak: 'Serie',
        metricTime: 'Tempo',
        metricLevel: 'Livello',
        trendUp: 'Su di {value}',
        trendDown: 'Giù di {value}',
        trendFlat: 'Nessun cambiamento',
        backToGames: 'Torna ai giochi',
        tutorialNext: 'Avanti',
        tutorialDone: 'Fatto',
        tutorialClose: 'Chiudi il tutorial',
        tutorialSteps: [
            ['SOPT Classic', 'Ricorda l’icona centrale e la direzione del bersaglio esterno.'],
            ['SOPT Advanced', 'Ricorda anche quale icona esterna era diversa: la terza risposta aumenta la difficoltà.'],
            ['Schede dei giochi', 'Espandi una scheda per vedere un esempio, impostare il suo livello e seguire i progressi giornalieri.'],
            ['Progressi', 'Qui trovi l’andamento complessivo e lo storico dei punteggi di ogni gioco.'],
        ],
    }),
    de: defineProgressLocale({
        title: 'Fortschritt',
        subtitle: 'Deine Ergebnisse im Zeitverlauf',
        overallTitle: 'Gesamtfortschritt',
        progressIndex: 'Fortschrittsindex',
        baselineHint: 'Verglichen mit deinem ersten Ergebnis',
        byGameTitle: 'Nach Spiel',
        current: 'Aktuell',
        best: 'Bestwert',
        results: 'Ergebnisse',
        noScoreYet: 'Noch kein Ergebnis',
        playToTrack: 'Spiele, um die Aufzeichnung zu starten',
        trackedGames: 'Erfasste Spiele',
        totalResults: 'Ergebnisse gesamt',
        metricScore: 'Punktzahl',
        metricStreak: 'Serie',
        metricTime: 'Zeit',
        metricLevel: 'Level',
        trendUp: 'Gestiegen um {value}',
        trendDown: 'Gesunken um {value}',
        trendFlat: 'Keine Änderung',
        backToGames: 'Zurück zu den Spielen',
        tutorialNext: 'Weiter',
        tutorialDone: 'Fertig',
        tutorialClose: 'Tutorial schließen',
        tutorialSteps: [
            ['SOPT Classic', 'Merke dir das Symbol in der Mitte und die Richtung des äußeren Ziels.'],
            ['SOPT Advanced', 'Merke dir zusätzlich das abweichende äußere Symbol – die dritte Antwort macht es schwieriger.'],
            ['Spielkarten', 'Klappe eine Karte auf, um ein Beispiel zu sehen, das eigene Level zu wählen und den Tagesfortschritt zu verfolgen.'],
            ['Fortschritt', 'Hier siehst du deinen Gesamttrend und den Punkteverlauf jedes Spiels.'],
        ],
    }),
    ja: defineProgressLocale({
        title: '進捗',
        subtitle: 'スコアの変化を確認',
        overallTitle: '全体の進捗',
        progressIndex: '進捗指数',
        baselineHint: '最初の結果との比較',
        byGameTitle: 'ゲーム別',
        current: '現在',
        best: 'ベスト',
        results: '結果',
        noScoreYet: 'スコアはまだありません',
        playToTrack: 'プレイすると記録が始まります',
        trackedGames: '記録中のゲーム',
        totalResults: '結果の合計',
        metricScore: 'スコア',
        metricStreak: '連続記録',
        metricTime: 'タイム',
        metricLevel: 'レベル',
        trendUp: '{value} 上昇',
        trendDown: '{value} 低下',
        trendFlat: '変化なし',
        backToGames: 'ゲームに戻る',
        tutorialNext: '次へ',
        tutorialDone: '完了',
        tutorialClose: 'チュートリアルを閉じる',
        tutorialSteps: [
            ['SOPT Classic', '中央のアイコンと、外側のターゲットがあった方向を覚えます。'],
            ['SOPT Advanced', '外側で異なっていたアイコンも覚えます。3つ目の回答で難しくなります。'],
            ['ゲームカード', 'カードを開くと、プレイ例、個別レベル、ゲームごとのデイリー進捗を確認できます。'],
            ['進捗', '全体の傾向と、各ゲームのスコア履歴をここで確認できます。'],
        ],
    }),
    ko: defineProgressLocale({
        title: '진행',
        subtitle: '시간에 따른 점수 변화',
        overallTitle: '전체 진행',
        progressIndex: '진행 지수',
        baselineHint: '첫 결과와 비교',
        byGameTitle: '게임별',
        current: '현재',
        best: '최고',
        results: '결과',
        noScoreYet: '아직 점수가 없습니다',
        playToTrack: '플레이하면 기록이 시작됩니다',
        trackedGames: '기록 중인 게임',
        totalResults: '전체 결과',
        metricScore: '점수',
        metricStreak: '연속 기록',
        metricTime: '시간',
        metricLevel: '레벨',
        trendUp: '{value} 상승',
        trendDown: '{value} 하락',
        trendFlat: '변화 없음',
        backToGames: '게임으로 돌아가기',
        tutorialNext: '다음',
        tutorialDone: '완료',
        tutorialClose: '튜토리얼 닫기',
        tutorialSteps: [
            ['SOPT Classic', '가운데 아이콘과 바깥쪽 목표의 방향을 기억하세요.'],
            ['SOPT Advanced', '달랐던 바깥쪽 아이콘도 기억하세요. 세 번째 답이 난이도를 높입니다.'],
            ['게임 카드', '카드를 펼치면 플레이 예시, 개별 레벨, 게임별 일일 진행을 볼 수 있습니다.'],
            ['진행', '전체 추세와 각 게임의 점수 기록을 여기에서 확인하세요.'],
        ],
    }),
    ar: defineProgressLocale({
        title: 'التقدم',
        subtitle: 'تغيّر نتائجك مع الوقت',
        overallTitle: 'التقدم الإجمالي',
        progressIndex: 'مؤشر التقدم',
        baselineHint: 'مقارنة بنتيجتك الأولى',
        byGameTitle: 'حسب اللعبة',
        current: 'الحالي',
        best: 'الأفضل',
        results: 'النتائج',
        noScoreYet: 'لا توجد نتيجة بعد',
        playToTrack: 'العب لبدء التتبع',
        trackedGames: 'الألعاب المتتبعة',
        totalResults: 'إجمالي النتائج',
        metricScore: 'النتيجة',
        metricStreak: 'السلسلة',
        metricTime: 'الوقت',
        metricLevel: 'المستوى',
        trendUp: 'ارتفاع {value}',
        trendDown: 'انخفاض {value}',
        trendFlat: 'بلا تغيير',
        backToGames: 'العودة إلى الألعاب',
        tutorialNext: 'التالي',
        tutorialDone: 'تم',
        tutorialClose: 'إغلاق الشرح',
        tutorialSteps: [
            ['SOPT Classic', 'تذكّر أيقونة الوسط واتجاه الهدف الخارجي.'],
            ['SOPT Advanced', 'تذكّر أيضًا الأيقونة الخارجية المختلفة؛ الإجابة الثالثة تزيد الصعوبة.'],
            ['بطاقات الألعاب', 'وسّع بطاقة أي لعبة لرؤية مثال اللعب وضبط مستواها ومتابعة تقدمها اليومي المستقل.'],
            ['التقدم', 'شاهد هنا اتجاهك الإجمالي وسجل نتائج كل لعبة.'],
        ],
    }),
    sv: defineProgressLocale({
        title: 'Framsteg',
        subtitle: 'Dina resultat över tid',
        overallTitle: 'Totala framsteg',
        progressIndex: 'Framstegsindex',
        baselineHint: 'Jämfört med ditt första resultat',
        byGameTitle: 'Per spel',
        current: 'Nuvarande',
        best: 'Bästa',
        results: 'Resultat',
        noScoreYet: 'Inget resultat ännu',
        playToTrack: 'Spela för att börja följa',
        trackedGames: 'Spårade spel',
        totalResults: 'Totalt antal resultat',
        metricScore: 'Poäng',
        metricStreak: 'Svit',
        metricTime: 'Tid',
        metricLevel: 'Nivå',
        trendUp: 'Upp {value}',
        trendDown: 'Ned {value}',
        trendFlat: 'Ingen förändring',
        backToGames: 'Tillbaka till spelen',
        tutorialNext: 'Nästa',
        tutorialDone: 'Klar',
        tutorialClose: 'Stäng guiden',
        tutorialSteps: [
            ['SOPT Classic', 'Kom ihåg mittikonen och riktningen till det yttre målet.'],
            ['SOPT Advanced', 'Kom också ihåg vilken yttre ikon som avvek; det tredje svaret gör rundan svårare.'],
            ['Spelkort', 'Fäll ut ett kort för att se ett spelexempel, ställa in egen nivå och följa spelets dagliga framsteg.'],
            ['Framsteg', 'Se din totala trend och varje spels resultathistorik här.'],
        ],
    }),
    no: defineProgressLocale({
        title: 'Fremgang',
        subtitle: 'Resultatene dine over tid',
        overallTitle: 'Samlet fremgang',
        progressIndex: 'Fremgangsindeks',
        baselineHint: 'Sammenlignet med ditt første resultat',
        byGameTitle: 'Per spill',
        current: 'Nåværende',
        best: 'Beste',
        results: 'Resultater',
        noScoreYet: 'Ingen poengsum ennå',
        playToTrack: 'Spill for å starte sporingen',
        trackedGames: 'Spill som spores',
        totalResults: 'Resultater totalt',
        metricScore: 'Poeng',
        metricStreak: 'Rekke',
        metricTime: 'Tid',
        metricLevel: 'Nivå',
        trendUp: 'Opp {value}',
        trendDown: 'Ned {value}',
        trendFlat: 'Ingen endring',
        backToGames: 'Tilbake til spillene',
        tutorialNext: 'Neste',
        tutorialDone: 'Ferdig',
        tutorialClose: 'Lukk veiledningen',
        tutorialSteps: [
            ['SOPT Classic', 'Husk ikonet i midten og retningen til målet ytterst.'],
            ['SOPT Advanced', 'Husk også hvilket ytre ikon som var annerledes; det tredje svaret gjør runden vanskeligere.'],
            ['Spillkort', 'Utvid et kort for å se et spilleksempel, velge eget nivå og følge spillets daglige fremgang.'],
            ['Fremgang', 'Se samlet trend og resultathistorikken for hvert spill her.'],
        ],
    }),
    da: defineProgressLocale({
        title: 'Fremskridt',
        subtitle: 'Dine resultater over tid',
        overallTitle: 'Samlede fremskridt',
        progressIndex: 'Fremskridtsindeks',
        baselineHint: 'Sammenlignet med dit første resultat',
        byGameTitle: 'Pr. spil',
        current: 'Aktuel',
        best: 'Bedste',
        results: 'Resultater',
        noScoreYet: 'Ingen score endnu',
        playToTrack: 'Spil for at starte registreringen',
        trackedGames: 'Registrerede spil',
        totalResults: 'Resultater i alt',
        metricScore: 'Score',
        metricStreak: 'Serie',
        metricTime: 'Tid',
        metricLevel: 'Niveau',
        trendUp: 'Steget {value}',
        trendDown: 'Faldet {value}',
        trendFlat: 'Ingen ændring',
        backToGames: 'Tilbage til spillene',
        tutorialNext: 'Næste',
        tutorialDone: 'Færdig',
        tutorialClose: 'Luk vejledningen',
        tutorialSteps: [
            ['SOPT Classic', 'Husk ikonet i midten og retningen til målet yderst.'],
            ['SOPT Advanced', 'Husk også hvilket ydre ikon der var anderledes; det tredje svar øger sværhedsgraden.'],
            ['Spilkort', 'Fold et kort ud for at se et eksempel, vælge eget niveau og følge spillets daglige fremskridt.'],
            ['Fremskridt', 'Se din samlede tendens og hvert spils resultathistorik her.'],
        ],
    }),
    nl: defineProgressLocale({
        title: 'Voortgang',
        subtitle: 'Je scores door de tijd',
        overallTitle: 'Totale voortgang',
        progressIndex: 'Voortgangsindex',
        baselineHint: 'Vergeleken met je eerste resultaat',
        byGameTitle: 'Per spel',
        current: 'Huidig',
        best: 'Beste',
        results: 'Resultaten',
        noScoreYet: 'Nog geen score',
        playToTrack: 'Speel om het bijhouden te starten',
        trackedGames: 'Gevolgde spellen',
        totalResults: 'Totaal aantal resultaten',
        metricScore: 'Score',
        metricStreak: 'Reeks',
        metricTime: 'Tijd',
        metricLevel: 'Niveau',
        trendUp: '{value} omhoog',
        trendDown: '{value} omlaag',
        trendFlat: 'Geen verandering',
        backToGames: 'Terug naar spellen',
        tutorialNext: 'Volgende',
        tutorialDone: 'Klaar',
        tutorialClose: 'Uitleg sluiten',
        tutorialSteps: [
            ['SOPT Classic', 'Onthoud het middelste pictogram en de richting van het buitenste doel.'],
            ['SOPT Advanced', 'Onthoud ook welk buitenste pictogram anders was; het derde antwoord maakt de ronde moeilijker.'],
            ['Spelkaarten', 'Klap een kaart open voor een voorbeeld, een eigen niveau en de dagelijkse voortgang van dat spel.'],
            ['Voortgang', 'Bekijk hier je algemene trend en de scoregeschiedenis van elk spel.'],
        ],
    }),
    pl: defineProgressLocale({
        title: 'Postępy',
        subtitle: 'Twoje wyniki w czasie',
        overallTitle: 'Ogólne postępy',
        progressIndex: 'Wskaźnik postępu',
        baselineHint: 'W porównaniu z pierwszym wynikiem',
        byGameTitle: 'Według gry',
        current: 'Obecny',
        best: 'Najlepszy',
        results: 'Wyniki',
        noScoreYet: 'Brak wyniku',
        playToTrack: 'Zagraj, aby rozpocząć śledzenie',
        trackedGames: 'Śledzone gry',
        totalResults: 'Wszystkie wyniki',
        metricScore: 'Wynik',
        metricStreak: 'Seria',
        metricTime: 'Czas',
        metricLevel: 'Poziom',
        trendUp: 'Wzrost o {value}',
        trendDown: 'Spadek o {value}',
        trendFlat: 'Bez zmian',
        backToGames: 'Wróć do gier',
        tutorialNext: 'Dalej',
        tutorialDone: 'Gotowe',
        tutorialClose: 'Zamknij samouczek',
        tutorialSteps: [
            ['SOPT Classic', 'Zapamiętaj ikonę pośrodku i kierunek zewnętrznego celu.'],
            ['SOPT Advanced', 'Zapamiętaj też, która zewnętrzna ikona była inna; trzecia odpowiedź zwiększa trudność.'],
            ['Karty gier', 'Rozwiń kartę, aby zobaczyć przykład, ustawić osobny poziom i śledzić dzienne postępy gry.'],
            ['Postępy', 'Tutaj zobaczysz ogólny trend i historię wyników każdej gry.'],
        ],
    }),
    tr: defineProgressLocale({
        title: 'İlerleme',
        subtitle: 'Zaman içindeki puanların',
        overallTitle: 'Genel ilerleme',
        progressIndex: 'İlerleme endeksi',
        baselineHint: 'İlk sonucunla karşılaştırma',
        byGameTitle: 'Oyuna göre',
        current: 'Güncel',
        best: 'En iyi',
        results: 'Sonuçlar',
        noScoreYet: 'Henüz puan yok',
        playToTrack: 'Takibi başlatmak için oyna',
        trackedGames: 'Takip edilen oyunlar',
        totalResults: 'Toplam sonuç',
        metricScore: 'Puan',
        metricStreak: 'Seri',
        metricTime: 'Süre',
        metricLevel: 'Seviye',
        trendUp: '{value} arttı',
        trendDown: '{value} azaldı',
        trendFlat: 'Değişiklik yok',
        backToGames: 'Oyunlara dön',
        tutorialNext: 'İleri',
        tutorialDone: 'Bitti',
        tutorialClose: 'Öğreticiyi kapat',
        tutorialSteps: [
            ['SOPT Classic', 'Ortadaki simgeyi ve dış hedefin yönünü hatırla.'],
            ['SOPT Advanced', 'Hangi dış simgenin farklı olduğunu da hatırla; üçüncü yanıt turu zorlaştırır.'],
            ['Oyun kartları', 'Oynanış örneğini görmek, ayrı seviyeyi ayarlamak ve günlük ilerlemeyi izlemek için kartı aç.'],
            ['İlerleme', 'Genel eğilimini ve her oyunun puan geçmişini burada gör.'],
        ],
    }),
    'zh-hans': defineProgressLocale({
        title: '进度',
        subtitle: '查看分数随时间的变化',
        overallTitle: '总体进度',
        progressIndex: '进度指数',
        baselineHint: '与首次结果相比',
        byGameTitle: '按游戏查看',
        current: '当前',
        best: '最佳',
        results: '结果',
        noScoreYet: '暂无分数',
        playToTrack: '开始游戏后即可记录',
        trackedGames: '已记录游戏',
        totalResults: '结果总数',
        metricScore: '分数',
        metricStreak: '连续纪录',
        metricTime: '时间',
        metricLevel: '等级',
        trendUp: '上升 {value}',
        trendDown: '下降 {value}',
        trendFlat: '没有变化',
        backToGames: '返回游戏',
        tutorialNext: '下一步',
        tutorialDone: '完成',
        tutorialClose: '关闭教程',
        tutorialSteps: [
            ['SOPT Classic', '记住中心图标以及外围目标所在的方向。'],
            ['SOPT Advanced', '还要记住哪个外围图标不同；第三个答案会提高难度。'],
            ['游戏卡片', '展开卡片即可查看玩法示例、设置独立等级并跟踪该游戏的每日进度。'],
            ['进度', '在这里查看总体趋势和每款游戏的分数记录。'],
        ],
    }),
    'zh-hant': defineProgressLocale({
        title: '進度',
        subtitle: '查看分數隨時間的變化',
        overallTitle: '整體進度',
        progressIndex: '進度指數',
        baselineHint: '與首次結果相比',
        byGameTitle: '依遊戲查看',
        current: '目前',
        best: '最佳',
        results: '結果',
        noScoreYet: '暫無分數',
        playToTrack: '開始遊戲後即可記錄',
        trackedGames: '已記錄遊戲',
        totalResults: '結果總數',
        metricScore: '分數',
        metricStreak: '連續紀錄',
        metricTime: '時間',
        metricLevel: '等級',
        trendUp: '上升 {value}',
        trendDown: '下降 {value}',
        trendFlat: '沒有變化',
        backToGames: '返回遊戲',
        tutorialNext: '下一步',
        tutorialDone: '完成',
        tutorialClose: '關閉教學',
        tutorialSteps: [
            ['SOPT Classic', '記住中央圖示以及外圍目標所在的方向。'],
            ['SOPT Advanced', '還要記住哪個外圍圖示不同；第三個答案會提高難度。'],
            ['遊戲卡片', '展開卡片即可查看玩法範例、設定獨立等級並追蹤該遊戲的每日進度。'],
            ['進度', '在這裡查看整體趨勢和每款遊戲的分數紀錄。'],
        ],
    }),
    ru: defineProgressLocale({
        title: 'Прогресс',
        subtitle: 'Ваши результаты со временем',
        overallTitle: 'Общий прогресс',
        progressIndex: 'Индекс прогресса',
        baselineHint: 'В сравнении с первым результатом',
        byGameTitle: 'По играм',
        current: 'Текущий',
        best: 'Лучший',
        results: 'Результаты',
        noScoreYet: 'Результатов пока нет',
        playToTrack: 'Сыграйте, чтобы начать отслеживание',
        trackedGames: 'Игр отслеживается',
        totalResults: 'Всего результатов',
        metricScore: 'Счёт',
        metricStreak: 'Серия',
        metricTime: 'Время',
        metricLevel: 'Уровень',
        trendUp: 'Рост на {value}',
        trendDown: 'Снижение на {value}',
        trendFlat: 'Без изменений',
        backToGames: 'Вернуться к играм',
        tutorialNext: 'Далее',
        tutorialDone: 'Готово',
        tutorialClose: 'Закрыть обучение',
        tutorialSteps: [
            ['SOPT Classic', 'Запомните значок в центре и направление внешней цели.'],
            ['SOPT Advanced', 'Запомните также отличающийся внешний значок: третий ответ повышает сложность.'],
            ['Карточки игр', 'Разверните карточку, чтобы увидеть пример, выбрать отдельный уровень и следить за дневным прогрессом.'],
            ['Прогресс', 'Здесь показаны общий тренд и история результатов каждой игры.'],
        ],
    }),
    id: defineProgressLocale({
        title: 'Progres',
        subtitle: 'Perubahan skormu dari waktu ke waktu',
        overallTitle: 'Progres keseluruhan',
        progressIndex: 'Indeks progres',
        baselineHint: 'Dibandingkan hasil pertamamu',
        byGameTitle: 'Per gim',
        current: 'Saat ini',
        best: 'Terbaik',
        results: 'Hasil',
        noScoreYet: 'Belum ada skor',
        playToTrack: 'Main untuk mulai melacak',
        trackedGames: 'Gim yang dilacak',
        totalResults: 'Total hasil',
        metricScore: 'Skor',
        metricStreak: 'Rentetan',
        metricTime: 'Waktu',
        metricLevel: 'Level',
        trendUp: 'Naik {value}',
        trendDown: 'Turun {value}',
        trendFlat: 'Tidak berubah',
        backToGames: 'Kembali ke gim',
        tutorialNext: 'Berikutnya',
        tutorialDone: 'Selesai',
        tutorialClose: 'Tutup tutorial',
        tutorialSteps: [
            ['SOPT Classic', 'Ingat ikon tengah dan arah target di bagian luar.'],
            ['SOPT Advanced', 'Ingat juga ikon luar yang berbeda; jawaban ketiga membuat putaran lebih sulit.'],
            ['Kartu gim', 'Buka kartu untuk melihat contoh, mengatur level tersendiri, dan mengikuti progres harian gim itu.'],
            ['Progres', 'Lihat tren keseluruhan dan riwayat skor setiap gim di sini.'],
        ],
    }),
    hi: defineProgressLocale({
        title: 'प्रगति',
        subtitle: 'समय के साथ आपके स्कोर',
        overallTitle: 'कुल प्रगति',
        progressIndex: 'प्रगति सूचकांक',
        baselineHint: 'आपके पहले परिणाम की तुलना में',
        byGameTitle: 'गेम के अनुसार',
        current: 'मौजूदा',
        best: 'सर्वश्रेष्ठ',
        results: 'परिणाम',
        noScoreYet: 'अभी कोई स्कोर नहीं',
        playToTrack: 'ट्रैकिंग शुरू करने के लिए खेलें',
        trackedGames: 'ट्रैक किए गए गेम',
        totalResults: 'कुल परिणाम',
        metricScore: 'स्कोर',
        metricStreak: 'सिलसिला',
        metricTime: 'समय',
        metricLevel: 'लेवल',
        trendUp: '{value} ऊपर',
        trendDown: '{value} नीचे',
        trendFlat: 'कोई बदलाव नहीं',
        backToGames: 'गेम पर वापस जाएँ',
        tutorialNext: 'आगे',
        tutorialDone: 'पूरा हुआ',
        tutorialClose: 'ट्यूटोरियल बंद करें',
        tutorialSteps: [
            ['SOPT Classic', 'बीच का आइकन और बाहरी लक्ष्य की दिशा याद रखें।'],
            ['SOPT Advanced', 'यह भी याद रखें कि कौन-सा बाहरी आइकन अलग था; तीसरा जवाब कठिनाई बढ़ाता है।'],
            ['गेम कार्ड', 'खेल का उदाहरण देखने, अलग लेवल चुनने और उस गेम की दैनिक प्रगति जानने के लिए कार्ड खोलें।'],
            ['प्रगति', 'यहाँ कुल रुझान और हर गेम का स्कोर इतिहास देखें।'],
        ],
    }),
});

const PROGRESS_LANGUAGE_ALIASES = Object.freeze({
    nb: 'no',
    nn: 'no',
    'no-nb': 'no',
    'pt-br': 'pt',
    'pt-pt': 'pt',
    'zh-cn': 'zh-hans',
    'zh-sg': 'zh-hans',
    'zh-hans-cn': 'zh-hans',
    'zh-hans-sg': 'zh-hans',
    'zh-tw': 'zh-hant',
    'zh-hk': 'zh-hant',
    'zh-mo': 'zh-hant',
    'zh-hant-tw': 'zh-hant',
    'zh-hant-hk': 'zh-hant',
});

const normalizeProgressLanguage = (language) => {
    const tag = `${language || ''}`.trim().replaceAll('_', '-').toLowerCase();

    if (Object.prototype.hasOwnProperty.call(PROGRESS_LOCALES, tag)) {
        return tag;
    }

    if (PROGRESS_LANGUAGE_ALIASES[tag]) {
        return PROGRESS_LANGUAGE_ALIASES[tag];
    }

    if (tag.startsWith('zh-')) {
        return /(?:hant|tw|hk|mo)/.test(tag) ? 'zh-hant' : 'zh-hans';
    }

    const base = tag.split('-')[0];

    return PROGRESS_LANGUAGE_ALIASES[base]
        || (Object.prototype.hasOwnProperty.call(PROGRESS_LOCALES, base) ? base : 'en');
};

export const getProgressCopy = (language = 'en') => (
    PROGRESS_LOCALES[normalizeProgressLanguage(language)]
);
